import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  Aangifte,
  BokkzAlgDataService,
  IncomeTaxEntity,
  JaarrekeningBalans,
  Onderneming
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, filter, Subscription, take } from 'rxjs';
import { BokkzIbDataService } from '@snelstart-ventures/fiscaal/domain-logic';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-omzetbelasting-passiva',
  templateUrl: './spec-omzetbelasting-passiva.component.html',
  imports: [
    SharedUiDialogModule,
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ],
  styleUrls: ['./spec-omzetbelasting-passiva.component.scss']
})
export class SpecOmzetbelastingPassivaComponent implements OnInit, OnDestroy {
  private routerSubscription: Subscription | undefined;
  private incomeTaxEntitySubscription: Subscription | undefined;
  private clientEntitySubscription: Subscription | undefined;
  private isSaving = false;
  @Input() public id!: string;
  public clientId!: string;
  public incomeTaxEntity = {} as IncomeTaxEntity;
  public partnerBoolean = {} as boolean;
  @Input() partner = new BehaviorSubject<boolean>(false);

  public ondernemingFG: FormGroup;
  public focusedElementId: string | null = null;
  public dialogVisible = false;
  public omzetbelastingPassivaFg = {} as FormGroup;

  showDialog() {
    this.dialogVisible = true;
  }

  constructor(
    public dataService: BokkzIbDataService, public dataAlgService: BokkzAlgDataService,
    private router: Router,
    private fb: FormBuilder) {
    this.ondernemingFG = this.fb.group({
      ondernemingen: this.fb.array([])
    });
    this.omzetbelastingPassivaFg = this.fb.group<JaarrekeningBalans>({
      omschrijving: 'omzetbelastingSchuld',
      waardeOpening: null as unknown as number,
      waardeSlot: null as unknown as number,
      omzetbelastingDitBoekjaar: null as unknown as number,
      omzetbelastingVorigBoekjaar: null as unknown as number,
      omzetbelastingOudereBoekjaren: null as unknown as number,
      nominaleWaarde: null as unknown as number
    });
  }

  //onderneming
  get ondernemingen(): FormArray {
    return this.ondernemingFG.get('ondernemingen') as FormArray;
  }

  createOndernemingFormGroup(onderneming?: Onderneming) {
    return this.dataService.createOndernemingFormGroup(onderneming);
  }

  getTotaalSchuldSpec(): number {
    const omzetbelastingDitBoekjaar = this.omzetbelastingPassivaFg.get('omzetbelastingDitBoekjaar')?.value || 0;
    const omzetbelastingVorigBoekjaar = this.omzetbelastingPassivaFg.get('omzetbelastingVorigBoekjaar')?.value || 0;
    const omzetbelastingOudereBoekjaren = this.omzetbelastingPassivaFg.get('omzetbelastingOudereBoekjaren')?.value || 0;

    return omzetbelastingDitBoekjaar + omzetbelastingVorigBoekjaar + omzetbelastingOudereBoekjaren;
  }

  ngOnInit(): void {
    this.incomeTaxEntitySubscription = this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
      this.incomeTaxEntity = incomeTaxEntity;
      if (incomeTaxEntity.aangifte) {
        this.updateForm();
        this.partner.subscribe((partner) => {
          this.partnerBoolean = partner;
          this.omzetbelastingPassivaFg.reset();
          const onderneming = !partner ? this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0] : this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen[0];
          const kortlopendeSchulden = onderneming?.kortlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'omzetbelastingSchuld') as JaarrekeningBalans;
          this.omzetbelastingPassivaFg.patchValue(kortlopendeSchulden);
          this.getTotaalSchuldSpec();
        });
      }
    });
    this.clientEntitySubscription = this.dataAlgService.client.subscribe((client) => {
      this.clientId = client.sstRelationId!;
    });
    this.routerSubscription = this.router.events.pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleUrlChange(event.urlAfterRedirects);
      });
  }


  private updateForm(): void {
    let ondernemingenLijst = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen || [];
    let kortlopendeschuldenOmzetbelastingFg = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0].kortlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'omzetbelastingSchuld');
    if (this.partnerBoolean) {
      ondernemingenLijst = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen || [];
      kortlopendeschuldenOmzetbelastingFg = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen[0]?.kortlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'omzetbelastingSchuld');
    }
    if (kortlopendeschuldenOmzetbelastingFg) {
      this.omzetbelastingPassivaFg.patchValue(kortlopendeschuldenOmzetbelastingFg);
      const formArray = this.ondernemingFG.get('ondernemingen') as FormArray;
      const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;

      while (formArray.length > ondernemingenLijst.length) {
        formArray.removeAt(formArray.length - 1);
      }

      // Update ondernemingen
      for (let i = 0; i < ondernemingenLijst.length; i++) {
        if (i < formArray.length) {
          const control = formArray.at(i) as FormGroup;
          control.patchValue(ondernemingenLijst[i]);
        } else {
          const formGroup = this.createOndernemingFormGroup(ondernemingenLijst[i]);
          formArray.push(formGroup);
        }
      }

      if (focusedControl && document.contains(focusedControl)) {
        focusedControl.focus();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
    }
    if (this.incomeTaxEntitySubscription) {
      this.incomeTaxEntitySubscription.unsubscribe(); // Unsubscribe from income tax entity
    }
    if (this.clientEntitySubscription) {
      this.clientEntitySubscription.unsubscribe();
    }
  }

  handleUrlChange(newUrl: string) {
    if (!this.isSaving && this.hasUnsavedChanges()) {
      this.isSaving = true;
      this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
      this.isSaving = false;
    }
  }

  private hasUnsavedChanges(): boolean {
    return this.omzetbelastingPassivaFg.dirty;
  }

  saveForm(): void {
    setTimeout(() => {
      if (!this.partnerBoolean) {
        this.omzetbelastingPassivaFg.updateValueAndValidity();
        const crediteurenFg = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0].kortlopendeSchulden?.at(0);
        const schuldenLoonheffingenFg = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0].kortlopendeSchulden?.at(2);
        const schuldenKortlopendOverigFg = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0].kortlopendeSchulden?.at(3);
        this.incomeTaxEntity.aangifte = {
          ...this.incomeTaxEntity.aangifte,
          box1: {
            ...this.incomeTaxEntity.aangifte?.box1,
            winstUitOnderneming: {
              ...this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming,
              ondernemingen: [
                {
                  ...this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0],
                  kortlopendeSchulden: [
                    crediteurenFg,
                    this.omzetbelastingPassivaFg.getRawValue(),
                    schuldenLoonheffingenFg,
                    schuldenKortlopendOverigFg
                  ]
                }
              ]
            }
          }
        } as Aangifte;
        this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
      } else {
        this.omzetbelastingPassivaFg.updateValueAndValidity();
        const crediteurenFg = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen[0].kortlopendeSchulden?.at(0);
        const schuldenLoonheffingenFg = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen[0].kortlopendeSchulden?.at(2);
        const schuldenKortlopendOverigFg = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen[0].kortlopendeSchulden?.at(3);
        this.incomeTaxEntity.aangifte = {
          ...this.incomeTaxEntity.aangifte,
          partnerAangifte: {
            ...this.incomeTaxEntity.aangifte?.partnerAangifte,
            box1: {
              ...this.incomeTaxEntity.aangifte?.partnerAangifte?.box1,
              winstUitOnderneming: {
                ...this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming,
                ondernemingen: [
                  {
                    ...this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen[0],
                    kortlopendeSchulden: [
                      crediteurenFg,
                      this.omzetbelastingPassivaFg.getRawValue(),
                      schuldenLoonheffingenFg,
                      schuldenKortlopendOverigFg
                    ]
                  }
                ]
              }
            }
          }
        } as Aangifte;
        this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
      }
    }, 0);
    this.getTotaalSchuldSpec();
  }

  public heeftOndernemingen(): boolean {
    if (this.partnerBoolean) {
      return !!(this.incomeTaxEntity &&
        this.incomeTaxEntity.aangifte?.partnerAangifte &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1 &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming.ondernemingen &&
        this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming.ondernemingen.length > 0);
    } else {
      return !!(this.incomeTaxEntity.aangifte &&
        this.incomeTaxEntity.aangifte?.box1 &&
        this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming &&
        this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming.ondernemingen &&
        this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming.ondernemingen.length > 0);
    }
  }
}
